import "~/styles/reset.css"
import "bootstrap/dist/css/bootstrap.min.css"
import "~/styles/common.css"
import "core-js"

import { AppProps } from "next/app"
import { NextComponentType } from "next"
import { useEffect, useState } from "react"
import DefaultLayout from "~/components/template/DefaultLayout"
import { Provider, useDispatch, useSelector } from "react-redux"
import { useRouter } from "next/router"
import { store } from "~/store"
import "dayjs/locale/ja"
import dayjs from "dayjs"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas } from "@fortawesome/free-solid-svg-icons"
import { far } from "@fortawesome/free-regular-svg-icons"
import { GlobalProvider } from "~/composable/useGlobal"
import { selectAuthorization, setUser } from "~/slices/authorizationSlice"
import { useRequest } from "~/api/Request"
import { setRedirect } from "~/slices/temporarySlice"
import { Socket } from "~/utils/socket"
import AutoLogout from "./autologout"
import * as Sentry from "@sentry/browser"
import Head from "next/head"
import { CompanyType } from "~/model/Company"
import { initGTM, trackSentryError, setGlobalUserId, clearGlobalUserId } from "~/utils/analytics"

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_APP_ENV,
  beforeSend(event) {
    trackSentryError(event)
    return event
  },
})

// @ts-ignore
library.add(fas)
// @ts-ignore
library.add(far)

let socket: Socket = new Socket()

function App({
  Component,
  pageProps,
}: AppProps & {
  Component: NextComponentType & {
    theme: "WHITE"
  }
  pageProps: any
} & any) {
  dayjs.locale("ja")
  const router = useRouter()

  useEffect(() => {
    initGTM()
  }, [])
  const allowUrl = [
    "^/video/.*?$",
    "^/sign/.*?$",
    "^/signdocuments/.*?$",
    "^/download/.*?$",
    "^/authentication/(signin|signup|forgot)/",
    "^/authentication/forgot/password/.*?/",
    "^/term",
    "^/unsignable-contract",
    "^/unsignable-contract/owner",
    "^/download-prior",
    "^/maintenance",
    "^/business/authentication/(signin|forgot)/",
    "^/resident/.*?$",
    "^/owner/authentication/(signin|forgot)/",
    "^/collector/authentication/(signin|forgot)/",
    "^/masterlease/authentication/(signin|forgot)/",
    "^/corporate/authentication/(signin|forgot)/",
    "^/koufuri/register",
    "^/koufuri/complete",
  ]
  const denyPages = {
    AGENT: ["/contracts/.*?/documents/add/", "^/management/other-accounts/"],
    MANAGEMENT: [],
  }

  const allowUpdateRolePath = [
    "^/management/accounts/.*?/complete",
    "^/management/business/",
    "^/management/corporate/",
    "^/management/owner-agency/",
    "^/management/collector-agency/",
    "^/management/masterlease/",
  ]

  const Layout = ({ children }) => {
    const ComponentLayout = DefaultLayout
    const user = useSelector(selectAuthorization).user

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (
          e.ctrlKey &&
          (e.keyCode === 61 ||
            e.keyCode === 107 ||
            e.keyCode === 173 ||
            e.keyCode === 109 ||
            e.keyCode === 187 ||
            e.keyCode === 189)
        ) {
          e.preventDefault()
        }
      }

      const handleWheel = (e) => {
        if (e.ctrlKey) {
          e.preventDefault()
        }
      }

      document.addEventListener("keydown", handleKeyDown)
      document.addEventListener("wheel", handleWheel, {
        passive: false,
      })

      return () => {
        document.removeEventListener("keydown", handleKeyDown)
        document.removeEventListener("wheel", handleWheel)
      }
    }, [])

    return (
      <ComponentLayout theme={Component.theme} key={"top"}>
        {children}
      </ComponentLayout>
    )
  }
  const Auth = ({ children }) => {
    const [loading, setLoading] = useState(true)
    const dispatch = useDispatch()
    const { Authorization, reloadToken } = useRequest()
    const user = useSelector(selectAuthorization).user
    useEffect(() => {
      if (router.isReady) {
        const load = async () => {
          const denyPage = denyPages[user?.company?.type]
          if (router.query.token) {
            reloadToken(router.query.token as string)
            const user = await Authorization.me()
            dispatch(setUser({ ...user, sl: true }))
            const { token, ...query } = router.query
            router.push({ pathname: router.pathname, query })
          } else if (!new RegExp(allowUrl.join("|")).test(location.pathname) && !user) {
            dispatch(
              setRedirect({
                pathname: router.pathname,
                query: router.query,
              })
            )
            if (router.pathname.includes("business")) {
              sessionStorage.setItem("businessContractId", JSON.stringify(router.query.id))
              router.push({ pathname: "/business/authentication/signin/" })
            } else if (router.pathname.includes("owner")) {
              router.push({ pathname: "/owner/authentication/signin/" })
            } else if (router.pathname.includes("collector")) {
              router.push({ pathname: "/collector/authentication/signin/" })
            } else if (router.pathname.includes("masterlease")) {
              router.push({ pathname: "/masterlease/authentication/signin/" })
            } else if (router.pathname.includes("corporate")) {
              sessionStorage.setItem("corporateContractId", JSON.stringify(router.query.id))
              router.push({ pathname: "/corporate/authentication/signin/" })
            } else {
              router.push({ pathname: "/authentication/signin/" })
            }
          } else if (
            denyPage &&
            denyPage.length &&
            new RegExp(denyPage.join("|")).test(location.pathname)
          ) {
            router.push({ pathname: "/404/" })
          } else {
            setLoading(false)
            if (user?.token && !socket.connected) {
              socket.connect(user?.token)
            }
          }
        }
        if (
          ![10, 1].includes(user?.roleId) &&
          router.pathname.match(/^\/management\//) &&
          !new RegExp(allowUpdateRolePath.join("|")).test(location.pathname)
        ) {
          router.push({ pathname: "/404" })
        } else if (
          //Only users with role 1 or 10 can access management/export-csv tab
          ![1, 10].includes(user?.roleId) &&
          router.pathname.match(/^\/management\/export-csv/) &&
          !new RegExp(allowUpdateRolePath.join("|")).test(location.pathname)
        ) {
          router.push({ pathname: "/404" })
        } else if (router.pathname === "/" && location.pathname !== "/") {
          if (router.asPath.includes("/video/")) {
            router.push({ pathname: location.pathname, query: router.query })
          } else {
            router.push({ pathname: location.pathname })
          }
        } else {
          load()
        }
      }
    }, [router])

    useEffect(() => {
      const companyTypeName =
        user?.company?.type === CompanyType.MANAGEMENT ? "管理会社" : user ? "仲介会社" : undefined

      let userScript

      if (user) {
        setGlobalUserId(user.id)
        userScript = document.createElement("script")
        userScript.innerHTML = `
        fs.clear();
        fs.show("1afb0517-488b-431b-8e1c-b909a13cafcc", ${user.id}, {
          company_name: "${user.company.name}",
          user_name: "${user.fullName}",
          user_email: "${user.email}",
          plan_name: "${companyTypeName}"
        });
        fs.tracking();
        fs.survey();
        fs.guide();
        fs.checklist();
        fs.tooltip();
      `
        document.head.appendChild(userScript)
      } else {
        clearGlobalUserId()
      }

      return () => {
        if (user) {
          document.head.removeChild(userScript)
        }
      }
    }, [user])
    return (
      <>
        <Head key="script">
          <script
            type="application/javascript"
            src="https://view.fullstar10.com/js/review.js"
          ></script>
        </Head>
        {!loading && <>{children}</>}
      </>
    )
  }
  return (
    <Provider store={store}>
      <GlobalProvider socket={socket}>
        {/* <AutoLogout> */}
        <Auth>
          <Layout>
            <Component {...pageProps} />
          </Layout>
        </Auth>
        {/* </AutoLogout> */}
      </GlobalProvider>
    </Provider>
  )
}

export default App
